/**
 * author: Meng Zong
 * Date: 2019/07/12
 *
 * This function can create a higher-order function which can get persisted data
 * and update new configuration.
 */

import React, { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPersistence, resetPersistence } from '@store/persistentConfig/actions';
import {
  persistenceResponseSelector,
  persistenceKeySelector,
} from '@store/persistentConfig/selectors';
import { Retry } from '@components/common';
import { DEFAULT_PERSISTENCE_STATE } from '@store/persistentConfig/reducer';

// to show loading layer on first render
function useFirstRender() {
  const firstRender = useRef(true);
  useEffect(() => {
    firstRender.current = false;
  }, []);
  return firstRender.current;
}

const WithPersistence = ({ children, ...props }) => {
  const dispatch = useDispatch();

  const [initializing, setInitializing] = useState(true);
  const extraProps: any = useMemo(() => ({}), []);

  const doneCallback = useCallback((applyAtomPersistence = null) => {
    if (applyAtomPersistence) {
      extraProps.applyAtomPersistence = applyAtomPersistence;
    }
    setInitializing(false);
  }, []);

  const persistenceData = useSelector(persistenceResponseSelector);
  const persistenceKey = useSelector(persistenceKeySelector);
  const firstRender = useFirstRender();

  const { skipGetAction } = persistenceData[persistenceKey] ?? DEFAULT_PERSISTENCE_STATE;

  useEffect(() => {
    // for scenarios where we are applying state manually
    if (skipGetAction) {
      // clear loading state
      dispatch(resetPersistence({ loading: false }));
      doneCallback();
    } else {
      dispatch(getPersistence(doneCallback));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // wait till persistence query is done
  // this avoids cancelled queries on trends load
  if (firstRender || initializing) {
    return <Retry isLoading showError={false} />;
  }
  const injectedChildren = cloneElement(children, { ...props, ...extraProps });
  return <>{injectedChildren}</>;
};

export default WithPersistence;
